import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { useCMSPageField } from '@/cms/fields'
import { ServiceItem } from '@/cms/fields/types/checkout-page'
import { useIntl } from '@/intl'
import { Custom500 } from '@/pages/500'
import { Basket } from '@/scopes/checkout/components/Basket'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutErrorDialog } from '@/scopes/checkout/components/CheckoutError/CheckoutErrorDialog'

import { CheckoutFooter } from '@/scopes/checkout/components/CheckoutFooter'

import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import {
  CheckoutHeading,
  CheckoutHeadingDescription,
  CheckoutHeadingTitle,
} from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutMessage } from '@/scopes/checkout/components/CheckoutMessage'
import NextStepButton from '@/scopes/checkout/components/CheckoutNextStep/NextStepButton'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { CheckoutUsps } from '@/scopes/checkout/components/CheckoutUsps/CheckoutUsps'
import { Content, Loader, MainContent, PageLayout, PrimaryContent, SideBarContent } from '@/scopes/checkout/components/Layout'
import { ConfirmActionWindow } from '@/scopes/checkout/components/PersonalDetailPage/ConfirmActionWindow'
import { PersonalDetailsForm } from '@/scopes/checkout/components/PersonalDetailsForm/Form'
import {
  confirmActionWindowIsOpenSelector,
  defaultValuesSelector,
  isFetchedSelector,
  isSubmittingSelector,
  shouldShowCheckoutErrorSelector,
} from '@/scopes/checkout/components/PersonalDetailsForm/selectors'
import { Preferences } from '@/scopes/checkout/components/Preferences/Preferences'
import { RyanairBanner } from '@/scopes/checkout/components/Ryanair/RyanairBanner'
import { GeneralTermsAndConditions } from '@/scopes/checkout/components/TermsAndConditions/GeneralTermsAndConditions'
import { participantsFormDataActions } from '@/scopes/checkout/domain/actions/participantsFormData'
import { preferencesDataActions } from '@/scopes/checkout/domain/actions/preferencesFormData'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext, useCheckoutContext } from '@/scopes/checkout/domain/CheckoutContext'
import { CheckoutOptimizelyFlagsProvider } from '@/scopes/checkout/domain/CheckoutOptimizelyFlagsProvider'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import {
  shouldShowConfirmationPopupIfAirlinesAreNotRyanair,
  shouldShowRyanairBanner,
} from '@/scopes/checkout/domain/policies/airline'
import {
  shouldShowConfirmationPopupOnPersonalDetailsStep,
} from '@/scopes/checkout/domain/policies/publicationCode'
import { hasAnyExternalFlight } from '@/scopes/checkout/domain/policies/transports'
import { bannerColorMap } from '@/scopes/checkout/domain/types'
import { ParticipantFormDataAndTermsAndConditions } from '@/scopes/checkout/domain/types/checkoutPersonalDetails'
import { createCheckoutAPIClient } from '@/scopes/checkout/infrastructure/api/checkoutAPIClient'
import { ServiceTypeApi } from '@/scopes/checkout/infrastructure/api/types'
import {
  CheckoutPersonalDetailsDependenciesProvider,
  useCheckoutPersonalDetailsActions,
} from '@/scopes/checkout/infrastructure/dependencies/dependencies'
import { participantsFormStepStore } from '@/scopes/checkout/infrastructure/store/participants-form-step-store'
import { useParticipantsFormStepStore } from '@/scopes/checkout/infrastructure/store/useParticipantsFormStepStore'
import { getAirlinesNames } from '@/scopes/checkout/pages/selectors'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import {
  scrollToFirstError,
} from '@/scopes/common/components/FormElements/utils/scrollToFirstError'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { createLocation } from '@/scopes/common/infrastructure/dom/location'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import { Faq } from '../components/Faq/Faq'
import { GeneralDataProtectionRegulation } from '../components/GeneralDataProtectionRegulation'
import { TrustIcons } from '../components/TrustIcons/TrustIcons'
import { shouldShowTrustIcons } from '../domain/policies/brand'

const queryClient = new QueryClient()
const checkoutMessageComponentIdsToOverride = [
  '228947cd-2c3e-45a7-b2d2-a4e4c1c243d9', // SZ
  '43c42e3d-b390-40e1-8d8b-c6d15bab6965', // BZ
]

function Page() {
  const brandName = useConfiguration('brandName')
  const sid = useCheckoutSid()
  const isFetched = useParticipantsFormStepStore(isFetchedSelector)
  const shouldShowError = useParticipantsFormStepStore(shouldShowCheckoutErrorSelector)
  const intl = useIntl()
  const actions = useCheckoutPersonalDetailsActions()
  const { data } = useBookingDraftQuery()
  const promotionTextSecondLine = intl.messages.checkout_steps_header_my_portal_promotion_text_line_2
  useSendCheckoutEvent('checkout_3_reached')
  const showTrustIcon = shouldShowTrustIcons(brandName)

  React.useEffect(() => {
    if (!sid)
      return
    const load = async () => {
      await actions.pageLoaded({ sid })
    }
    load()
  }, [actions, sid])

  if (data?.error && data.error.hasError) {
    return (
      <PageLayout>
        <CheckoutError />
      </PageLayout>
    )
  }

  if (shouldShowError) {
    return (
      <PageLayout>
        <CheckoutErrorDialog
          textId="checkout_steps_personal_details_error_title_text"
          supportingTextId="checkout_steps_personal_details_error_description_text"
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
        <CheckoutHeader />
        <MainContent>
          {!isFetched
            ? <Loader />
            : (
                <>
                  <CheckoutProgress />
                  <CheckoutHeading>
                    <CheckoutHeadingTitle>
                      {intl.formatMessage({ id: 'checkout_steps_header_step_3_title' })}
                    </CheckoutHeadingTitle>
                    <CheckoutHeadingDescription>
                      {intl.formatMessage({ id: 'checkout_steps_header_my_portal_promotion_text_line_1' })}
                      {promotionTextSecondLine && (
                        <>
                          <br />
                          {intl.formatMessage({ id: 'checkout_steps_header_my_portal_promotion_text_line_2' })}
                        </>
                      )}
                    </CheckoutHeadingDescription>
                  </CheckoutHeading>
                  <Content>
                    <PrimaryContent>
                      <PageSections />
                    </PrimaryContent>
                    <SideBarContent>
                      <Basket />
                      <div>
                        <Faq />
                        {showTrustIcon && <TrustIcons />}
                      </div>
                    </SideBarContent>
                  </Content>
                </>
              )}
        </MainContent>
        {isFetched && <CheckoutFooter />}
      </NewRelicErrorBoundary>
    </PageLayout>
  )
}

function getTermsAndConditionsItems(items: ServiceItem[]) {
  return items.filter(item => item.type === 'TermsAndConditions')
}

function buildTermsAndConditionsDefaultValues(items: ServiceItem[]) {
  return items.reduce<Record<string, boolean>>((acc, item) => {
    acc[item.componentId] = false
    return acc
  }, {})
}

function PageSections() {
  const defaultValues = useParticipantsFormStepStore(defaultValuesSelector)
  const isBeingSubmitted = useParticipantsFormStepStore(isSubmittingSelector)
  const isShowingActionWindow = useParticipantsFormStepStore(confirmActionWindowIsOpenSelector)
  const items = useCMSPageField('Checkout.items')?.items ?? []
  const termsAndConditionsItems = getTermsAndConditionsItems(items)
  const actions = useCheckoutPersonalDetailsActions()
  const sid = useCheckoutSid()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const publicationCode = useConfiguration('publicationCode')
  const airlinesNames = getAirlinesNames(bookingDraftData?.transportInfo)
  const shouldShowConfirmationWindow = shouldShowConfirmationPopupOnPersonalDetailsStep(publicationCode) && shouldShowConfirmationPopupIfAirlinesAreNotRyanair(airlinesNames)
  const showLoadingAndDisableState = shouldShowConfirmationWindow ? isBeingSubmitted : (isBeingSubmitted || isShowingActionWindow)
  const showRyanairBanner = shouldShowRyanairBanner(airlinesNames)
  const { showMandatoryPaymentPage, showMandatoryPaymentPageUds } = useOptimizelyFlagContext()

  const methods = useForm({
    mode: 'onBlur',
    defaultValues: {
      ...defaultValues,
      termsAndConditions: buildTermsAndConditionsDefaultValues(termsAndConditionsItems),
    },
  })

  const { handleSubmit } = methods

  const onSubmit = async (data: ParticipantFormDataAndTermsAndConditions) => {
    const result = await actions.participantsFormDataSubmitted({
      sid,
      participantFormData: data,
      preferences: bookingDraftData?.preferences || null,
    })

    if (!result.success) {
      return
    }

    const shouldRedirectToMandatoryPayment = shouldShowConfirmationWindow && showMandatoryPaymentPage && hasAnyExternalFlight(bookingDraftData?.transportInfo)

    if (shouldRedirectToMandatoryPayment) {
      actions.redirectToMandatoryPaymentStepSZabTest()
    }
    else if (!shouldShowConfirmationWindow) {
      if (showMandatoryPaymentPageUds) {
        actions.redirectToMandatoryPaymentStepUds()
      }
      else {
        actions.redirectToNextStep()
      }
    }
  }

  const onInvalid = (errors: Record<string, any>) => {
    scrollToFirstError(errors)
  }

  if (!items) {
    return null
  }
  return (
    <FormProvider {...methods}>
      {items.map((item, index) => {
        const { type, componentId } = item

        switch (type) {
          case ServiceTypeApi.ConditionalMessages:
            if (showMandatoryPaymentPage && checkoutMessageComponentIdsToOverride.includes(componentId) && hasAnyExternalFlight(bookingDraftData?.transportInfo)) {
              return <CustomBannerForABTestMandatoryPaymentPage key="customMandatoryPaymentBanner" />
            }
            return <CheckoutMessage key={`${type}-${index}`} componentId={componentId} />
          case ServiceTypeApi.ParticipantsForm:
            return <PersonalDetailsForm key={`${type}-${index}`} />
          case ServiceTypeApi.Preferences:
            return <Preferences item={item} key={`${type}-${index}`} />
          case ServiceTypeApi.TermsAndConditions:
            return (
              <GeneralTermsAndConditions
                items={termsAndConditionsItems}
                fieldName="termsAndConditions"
                key={`${type}-${index}`}
              />
            )
          default:
            if (window.newrelic) {
              const error = new Error(`[CheckoutSteps] No service ${type} found`)
              window.newrelic.noticeError(error)
            }
            return null
        }
      })}
      <GeneralDataProtectionRegulation />
      {showRyanairBanner && <RyanairBanner />}
      <NextStepButton
        // eslint-disable-next-line ts/no-non-null-asserted-optional-chain -- Always present
        text={bookingDraftData?.navigation?.nextStep?.text!}
        disabled={showLoadingAndDisableState}
        isLoading={showLoadingAndDisableState}
        onClick={handleSubmit(onSubmit, onInvalid)}
      />
      <CheckoutUsps />
      {shouldShowConfirmationWindow
      && !(showMandatoryPaymentPage && hasAnyExternalFlight(bookingDraftData?.transportInfo)) && <ConfirmActionWindow />}
    </FormProvider>
  )
}

export default function CheckoutPageStep3() {
  const contextItemId = useCMSPageField('contextItemId')
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    step: 3,
  }), [contextItemId])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <CheckoutOptimizelyFlagsProvider>
          <PageProvider>
            <MetaTitle titleTranslationTag="checkout_steps_step_3_meta_title" />
            <Page />
          </PageProvider>
          <DevTools />
        </CheckoutOptimizelyFlagsProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}

function PageProvider({ children }: React.PropsWithChildren) {
  const checkoutContext = useCheckoutContext()
  const participantsFormContextItemId = useCMSPageField('Checkout.participantsFormContextItemId')
  const checkoutApiClient = createCheckoutAPIClient({
    apiContextParameters: {
      ...checkoutContext,
      participantsFormContextItemId: participantsFormContextItemId!,
    },
  })
  const newRelicLogger = createNewRelicLogger({ scope: 'checkout' })

  const actions = participantsFormDataActions({
    store: participantsFormStepStore,
    client: checkoutApiClient,
    preferenceActions: preferencesDataActions({
      client: checkoutApiClient,
      logger: newRelicLogger,
    }),
    logger: newRelicLogger,
    location: createLocation(),
  })

  return (
    <CheckoutPersonalDetailsDependenciesProvider dependencies={{ actions }}>
      {children}
    </CheckoutPersonalDetailsDependenciesProvider>
  )
}

function CustomBannerForABTestMandatoryPaymentPage() {
  const intl = useIntl()
  return (
    <Banner
      color={bannerColorMap.info as BannerColors}
      size="M"
      hasIcon
    >
      <Banner.TextsWrapper>
        <Banner.Text>{intl.formatMessage({ id: 'checkout_steps_custom_message_ab_test_mandatory_payment_title' })}</Banner.Text>
        <Banner.SupportingText innerHTML={intl.formatMessage({ id: 'checkout_steps_custom_message_ab_test_mandatory_payment_text' })} />
      </Banner.TextsWrapper>
    </Banner>
  )
}
